// TIAFA Family Tree Maker (v0.6.0) - UI & Chart Controller with Public Share / Read-Only
import { familyService, getInitialsSvg } from './services/familyService';
import { db } from './db/indexedDB';
import { imgService } from './services/imgbb';
import { neonService } from './services/neonService';
import { Relationship } from './types';
import { authService, AuthUser } from './services/authService';

// Global from /vendor/family-chart.min.js (index.html script tag)
declare const f3: any;

let chart: any = null;
let editTree: any = null;
let isProgrammaticUpdate = false;
let currentUser: AuthUser | null = null;

const LOGIN_BG_IMAGES = [
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1600',
  'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=1600',
  'https://images.unsplash.com/photo-1481627834876-b7833e1d8773?w=1600',
  'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=1600',
];

// === Public Share / Read-Only Mode State ===
let isReadOnly = false;
let sharedFamilyId: string | null = null;

function detectShareRoute(): { isShare: boolean; familyId: string | null } {
  const path = window.location.pathname;
  const match = path.match(/^\/share\/([0-9a-f-]+)\/?$/i);
  if (match) {
    return { isShare: true, familyId: match[1] };
  }
  return { isShare: false, familyId: null };
}

function showReadOnlyBadge(familyName?: string) {
  const existing = document.getElementById('readOnlyBadge');
  if (existing) existing.remove();

  const badge = document.createElement('div');
  badge.id = 'readOnlyBadge';
  badge.className = 'notranslate';
  badge.style.cssText = `
    display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;
    margin-bottom:15px;padding:12px 16px;border-radius:10px;
    background:rgba(33,150,243,0.12);border:1px solid rgba(33,150,243,0.35);
    color:var(--text-color,#e0e0e0);`;
  badge.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:4px;">
      <strong style="font-size:14px;">👁️ Viewing Shared Tree (Read Only)</strong>
      <span style="font-size:12px;opacity:0.85;">${familyName ? `Family: ${familyName}` : 'Public share link'} — editing is disabled</span>
    </div>
    <a href="/" style="padding:8px 14px;background:#2196F3;color:white;text-decoration:none;border-radius:6px;font-size:13px;font-weight:600;">
      🌳 Open My Tree
    </a>
  `;

  const app = document.getElementById('app');
  const chartEl = document.getElementById('chart-container');
  if (app && chartEl) {
    app.insertBefore(badge, chartEl);
  }
}

// === Helper: Handle Image Upload UI Flow ===
async function handleImageUploadFlow(
  fileInput: HTMLInputElement,
  previewEl: HTMLDivElement,
  onSuccess: (url: string) => void
) {
  const file = fileInput.files?.[0];
  if (!file) return;

  previewEl.style.display = 'block';
  previewEl.style.backgroundImage = 'none';
  previewEl.innerHTML = '<span style="font-size:10px;color:#fff;text-align:center;display:block;line-height:50px;">⏳...</span>';
  previewEl.style.backgroundColor = 'rgba(0,0,0,0.5)';

  try {
    const res = await imgService.uploadPhoto(file);
    previewEl.innerHTML = '';
    previewEl.style.backgroundImage = `url('${res.thumb}')`;
    previewEl.style.backgroundSize = 'cover';
    previewEl.style.backgroundPosition = 'center';
    onSuccess(res.thumb);
  } catch (error) {
    console.error(error);
    alert('Photo upload failed: ' + (error as Error).message);
    previewEl.style.display = 'none';
    previewEl.innerHTML = '';
  }
}

// === Render Chart ===
async function renderChart() {
  const container = document.getElementById('chart-container');
  if (!container) return;

  isProgrammaticUpdate = true;

  try {
    const families = await familyService.getFamilies();
    if (families.length === 0 && !isReadOnly) {
      await familyService.seedDemoData();
    }

    const select = document.getElementById('familySelector') as HTMLSelectElement;
    let familyId = isReadOnly ? (sharedFamilyId || '') : (select?.value || '');

    let family: any = familyId ? await familyService.getFamily(familyId) : undefined;
    if (!family && !isReadOnly) {
      const list = await familyService.getFamilies();
      family = list[0];
      if (family && select) select.value = family.id;
    }

    if (!family) {
      container.innerHTML =
        '<p style="color:white;padding:20px;text-align:center;">No family found to render.</p>';
      return;
    }

    await familyService.repairFamily(family.id);

    const members = await familyService.getMembersByFamily(family.id);
    const relationships = await familyService.getRelationshipsByFamily(family.id);
    const libraryData = familyService.toLibraryFormat(members, relationships);

    if (!chart) {
      chart = f3.createChart(container, libraryData);

      if (!isReadOnly) {
        editTree = chart
          .editTree()
          .setFields(['first name', 'last name', 'birthday', 'gender', 'avatar'])
          .setEditFirst(true);

        editTree.setOnChange(async () => {
          if (isProgrammaticUpdate) {
            console.log('🛡️ Skip onChange (programmatic)');
            return;
          }
          if (editTree.isAddingRelative?.() || editTree.isRemovingRelative?.()) {
            console.log('🛡️ Skip onChange (add/remove relative in progress)');
            return;
          }

          const updatedData = editTree.exportData();
          const currentFamilyId =
            (document.getElementById('familySelector') as HTMLSelectElement)?.value ||
            family.id;

          if (currentFamilyId && updatedData) {
            await familyService.syncFromLibraryFormat(currentFamilyId, updatedData);
            await renderChart();
          }
        });
      }

      const card = chart
        .setCardHtml()
        .setCardDisplay([['first name', 'last name'], ['birthday']])
        .setStyle('imageCircleRect');

      card.setOnCardClick((_e: any, d: any) => {
        if (!isReadOnly && (d.data.to_add || d.data._new_rel_data)) {
          editTree?.open(d.data);
          return;
        }
        if (d.data.to_add || d.data._new_rel_data) return;

        familyService.getMember(d.data.id).then((member) => {
          if (member) showMemberDetails(member);
        });
      });
    } else {
      chart.updateData(libraryData);
      if (editTree && !isReadOnly) {
        editTree.store.updateData(libraryData);
      }
    }

    chart.updateTree({ initial: true });
    if (!isReadOnly) {
      await updateRelatedDropdown();
    }
    console.log('✅ Chart rendered successfully!');
  } catch (error) {
    console.error('❌ Error:', error);
    container.innerHTML = `<p style="color:red;padding:20px;">Error: ${(error as Error).message}</p>`;
  } finally {
    setTimeout(() => {
      isProgrammaticUpdate = false;
    }, 300);
  }
}

// === Show Member Details ===
function showMemberDetails(member: any) {
  document.getElementById('memberDetailView')?.remove();

  const el = document.createElement('div');
  el.id = 'memberDetailView';
  el.className = 'custom-popup';
  el.style.cssText = `
    position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
    background:var(--card-bg, white);color:var(--text-color, #333);
    padding:30px;border-radius:16px;
    box-shadow:0 4px 60px rgba(0,0,0,0.4);z-index:1000;
    min-width:420px;max-width:90%;max-height:90vh;overflow-y:auto;
    border:1px solid var(--border-color, #eee);`;

  const avatarUrl = member.avatar || getInitialsSvg(member.name, member.gender);

  const statusBadge = member.isDeceased 
    ? `<span style="font-size:12px;color:#f44336;background:rgba(244,67,54,0.1);padding:3px 8px;border-radius:12px;font-weight:600;display:inline-block;margin-top:4px;">🕊️ Deceased (${member.deathYear || 'N/A'})</span>`
    : `<span style="font-size:12px;color:#4CAF50;background:rgba(76,175,80,0.1);padding:3px 8px;border-radius:12px;font-weight:600;display:inline-block;margin-top:4px;">🟢 Living</span>`;

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:start;margin-bottom:20px;border-bottom:2px solid var(--border-color, #f0f0f0);padding-bottom:15px;">
      <div style="display:flex;gap:15px;align-items:center;">
        <img src="${avatarUrl}" style="width:70px;height:70px;border-radius:50%;object-fit:cover;box-shadow:0 2px 8px rgba(0,0,0,0.15);border:2px solid var(--border-color, #eee);" />
        <div>
          <h2 style="margin:0;font-size:22px;font-weight:700;">${member.name}</h2>
          ${statusBadge}
        </div>
      </div>
      <div style="display:flex;gap:8px;">
        ${isReadOnly ? '' : `<button id="detailEditBtn" style="padding:6px 14px;background:#2196F3;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:600;">✏️ Edit</button>`}
        <button id="detailCloseBtn" style="background:none;border:none;font-size:28px;cursor:pointer;color:var(--text-muted, #999);">&times;</button>
      </div>
    </div>

    <!-- Details Grid -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px;">
      <div style="background:var(--bg-color, #f8f9fa);padding:12px;border-radius:8px;">
        <label style="font-size:11px;color:var(--text-muted, #999);display:block;margin-bottom:4px;">Gender</label>
        <span style="font-size:15px;font-weight:600;">${member.gender === 'M' ? '👨 Male' : '👩 Female'}</span>
      </div>
      <div style="background:var(--bg-color, #f8f9fa);padding:12px;border-radius:8px;">
        <label style="font-size:11px;color:var(--text-muted, #999);display:block;margin-bottom:4px;">Birth Year</label>
        <span style="font-size:15px;font-weight:600;">${member.birthYear || '-'}</span>
      </div>
      <div style="background:var(--bg-color, #f8f9fa);padding:12px;border-radius:8px;">
        <label style="font-size:11px;color:var(--text-muted, #999);display:block;margin-bottom:4px;">Qualification</label>
        <span style="font-size:15px;font-weight:600;">${member.qualification || '-'}</span>
      </div>
      <div style="background:var(--bg-color, #f8f9fa);padding:12px;border-radius:8px;">
        <label style="font-size:11px;color:var(--text-muted, #999);display:block;margin-bottom:4px;">Profession</label>
        <span style="font-size:15px;font-weight:600;">${member.profession || '-'}</span>
      </div>
    </div>

    <!-- Biography Section -->
    ${member.bio ? `
    <div style="margin-bottom:20px;padding:12px;background:rgba(33,150,243,0.05);border-left:4px solid #2196F3;border-radius:0 8px 8px 0;">
      <h5 style="margin:0 0 6px 0;font-size:12px;color:#2196F3;text-transform:uppercase;letter-spacing:0.5px;">📖 Biography / Story</h5>
      <p style="margin:0;font-size:14px;line-height:1.5;font-style:italic;">"${member.bio}"</p>
    </div>` : ''}

    <div style="margin-top:20px;padding-top:15px;border-top:1px solid var(--border-color, #eee);">
      <h4 style="margin:0 0 10px 0;color:var(--text-muted, #555);font-size:14px;text-transform:uppercase;letter-spacing:0.5px;">📋 Relationships</h4>
      <div id="detailRelationships" style="font-size:14px;">Loading...</div>
    </div>`;

  document.body.appendChild(el);

  (async () => {
    const rels = await familyService.getRelationshipsByMember(member.id);
    const members = await familyService.getMembersByFamily(member.familyId);
    const box = document.getElementById('detailRelationships');
    if (!box) return;
    if (rels.length === 0) {
      box.innerHTML = '<span style="color:var(--text-muted, #aaa);">No relationships found</span>';
      return;
    }
    let html = '<ul style="list-style:none;padding:0;margin:0;">';
    for (const rel of rels) {
      const otherId = rel.sourceId === member.id ? rel.targetId : rel.sourceId;
      const other = members.find((m) => m.id === otherId);
      if (!other) continue;
      let label: string = rel.type;
      let statusTag = '';

      if (rel.type === 'spouse') {
        const status = rel.metadata?.spouseStatus || 'married';
        const emojiMap: Record<string, string> = { married: '💍', divorced: '💔', separated: '🤝', widowed: '🕊️' };
        statusTag = ` <span style="font-size:11px;color:var(--text-muted);background:#eee;padding:2px 6px;border-radius:4px;text-transform:capitalize;">${emojiMap[status] || '💍'} ${status}</span>`;
        label = '💑 Spouse';
      } else if (rel.type === 'parent') {
        label = rel.sourceId === member.id ? '👨👩 Parent of' : '👶 Child of';
      }
      
      html += `<li style="padding:8px 12px;border-bottom:1px solid var(--border-color, #f0f0f0);display:flex;justify-content:space-between;align-items:center;">
        <span style="color:var(--text-muted);">${label}</span>
        <span style="font-weight:600;">${other.name}${statusTag}</span>
      </li>`;
    }
    box.innerHTML = html + '</ul>';
  })();

  document.getElementById('detailCloseBtn')?.addEventListener('click', () => el.remove());

  if (!isReadOnly) {
    document.getElementById('detailEditBtn')?.addEventListener('click', () => {
      el.remove();
      showEditForm(member);
    });
  }
}

function showEditForm(member: any) {
  if (isReadOnly) return; // Guard clause

  document.getElementById('editMemberForm')?.remove();
  const el = document.createElement('div');
  el.id = 'editMemberForm';
  el.className = 'custom-popup';
  el.style.cssText = `
    position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
    background:var(--card-bg, white);color:var(--text-color, #333);
    padding:30px;border-radius:12px;
    box-shadow:0 4px 40px rgba(0,0,0,0.3);z-index:1000;width:400px;
    max-height:90vh;overflow-y:auto;
    border:1px solid var(--border-color, #eee);`;

  const avatarUrl = member.avatar || getInitialsSvg(member.name, member.gender);
  let tempAvatarUrl = member.avatar;

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;">
      <h3 style="margin:0;font-weight:700;">✏️ Edit Member</h3>
      <button id="closeEditForm" style="background:none;border:none;font-size:24px;cursor:pointer;color:var(--text-muted, #999);">&times;</button>
    </div>
    
    <div style="display:flex;gap:15px;align-items:center;margin-bottom:15px;background:var(--bg-color, #f9f9f9);padding:10px;border-radius:8px;">
      <div id="editPhotoPreview" style="width:60px;height:60px;border-radius:50%;background-image:url('${avatarUrl}');background-size:cover;background-position:center;box-shadow:0 2px 6px rgba(0,0,0,0.15);flex-shrink:0;"></div>
      <div>
        <label style="display:block;font-size:11px;color:var(--text-muted, #666);margin-bottom:4px;font-weight:600;">Profile Photo</label>
        <input type="file" id="editPhotoInput" accept="image/*" style="display:none;" />
        <button id="triggerEditPhotoUpload" style="padding:4px 10px;background:#eee;color:#333;border:1px solid #ccc;border-radius:4px;font-size:11px;cursor:pointer;font-weight:600;">Change Photo</button>
      </div>
    </div>

    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Full Name</label>
      <input id="editName" type="text" value="${member.name}" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
    </div>
    
    <div style="display:flex;gap:10px;margin-bottom:12px;">
      <div style="flex:1;">
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Gender</label>
        <select id="editGender" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);">
          <option value="M" ${member.gender === 'M' ? 'selected' : ''}>Male</option>
          <option value="F" ${member.gender === 'F' ? 'selected' : ''}>Female</option>
        </select>
      </div>
      <div style="flex:1;">
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Birth Year</label>
        <input id="editBirth" type="text" value="${member.birthYear || ''}" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
      </div>
    </div>

    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Qualification</label>
      <input id="editQualification" type="text" value="${member.qualification || ''}" placeholder="e.g. B.Tech, High School" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
    </div>
    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Profession</label>
      <input id="editProfession" type="text" value="${member.profession || ''}" placeholder="e.g. Businessman, Housewife" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
    </div>

    <div style="margin-bottom:12px;background:#fefefe;padding:10px;border:1px solid var(--border-color,#eee);border-radius:6px;">
      <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;font-weight:600;color:var(--text-color);">
        <input type="checkbox" id="editIsDeceased" ${member.isDeceased ? 'checked' : ''} style="transform:scale(1.1);" />
        🕊️ Is Deceased
      </label>
      <div id="deathYearContainer" style="margin-top:8px;display:${member.isDeceased ? 'block' : 'none'};">
        <label style="display:block;font-size:11px;color:var(--text-muted, #666);margin-bottom:4px;">Death Year</label>
        <input id="editDeath" type="text" value="${member.deathYear || ''}" placeholder="e.g. 2021" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
      </div>
    </div>

    <div id="editSpouseRelationshipsContainer" style="margin-bottom:12px;display:none;">
      <label style="display:block;font-size:12px;font-weight:600;color:var(--text-muted, #666);margin-bottom:6px;">💍 Spouse Status</label>
      <div id="spousesList"></div>
    </div>

    <div style="margin-bottom:18px;">
      <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Biography / Notes</label>
      <textarea id="editBio" rows="3" placeholder="Write memories, native village, stories..." style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);font-family:inherit;font-size:13px;box-sizing:border-box;resize:none;">${member.bio || ''}</textarea>
    </div>

    <div style="display:flex;gap:10px;">
      <button id="saveEditBtn" style="flex:1;padding:10px;background:#4CAF50;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">Save Changes</button>
      <button id="deleteMemberBtn" style="flex:1;padding:10px;background:#f44336;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">Delete</button>
    </div>`;

  document.body.appendChild(el);
  document.getElementById('closeEditForm')?.addEventListener('click', () => el.remove());

  const isDeceasedCheckbox = document.getElementById('editIsDeceased') as HTMLInputElement;
  const deathContainer = document.getElementById('deathYearContainer') as HTMLDivElement;
  isDeceasedCheckbox.addEventListener('change', () => {
    deathContainer.style.display = isDeceasedCheckbox.checked ? 'block' : 'none';
  });

  const spousesContainer = document.getElementById('editSpouseRelationshipsContainer') as HTMLDivElement;
  const spousesList = document.getElementById('spousesList') as HTMLDivElement;
  let activeSpouseRels: any[] = [];

  (async () => {
    const rels = await familyService.getRelationshipsByMember(member.id);
    const members = await familyService.getMembersByFamily(member.familyId);
    activeSpouseRels = rels.filter(r => r.type === 'spouse');

    if (activeSpouseRels.length > 0) {
      spousesContainer.style.display = 'block';
      let html = '';
      activeSpouseRels.forEach((rel) => {
        const otherId = rel.sourceId === member.id ? rel.targetId : rel.sourceId;
        const partner = members.find(m => m.id === otherId);
        if (partner) {
          const currentStatus = rel.metadata?.spouseStatus || 'married';
          html += `
            <div style="display:flex;justify-content:space-between;align-items:center;background:#f8f9fa;padding:8px 10px;border-radius:6px;margin-bottom:6px;border:1px solid #eee;">
              <span style="font-size:13px;font-weight:500;">${partner.name}</span>
              <select class="spouse-status-select" data-rel-id="${rel.id}" style="padding:4px 8px;font-size:12px;border-radius:4px;border:1px solid #ccc;">
                <option value="married" ${currentStatus === 'married' ? 'selected' : ''}>Married</option>
                <option value="divorced" ${currentStatus === 'divorced' ? 'selected' : ''}>Divorced</option>
                <option value="separated" ${currentStatus === 'separated' ? 'selected' : ''}>Separated</option>
                <option value="widowed" ${currentStatus === 'widowed' ? 'selected' : ''}>Widowed</option>
              </select>
            </div>`;
        }
      });
      spousesList.innerHTML = html;
    }
  })();

  const fileInput = document.getElementById('editPhotoInput') as HTMLInputElement;
  const triggerBtn = document.getElementById('triggerEditPhotoUpload');
  const previewEl = document.getElementById('editPhotoPreview') as HTMLDivElement;

  triggerBtn?.addEventListener('click', () => fileInput.click());
  fileInput?.addEventListener('change', async () => {
    await handleImageUploadFlow(fileInput, previewEl, (url) => {
      tempAvatarUrl = url;
    });
  });

  document.getElementById('saveEditBtn')?.addEventListener('click', async () => {
    const isDeceasedVal = isDeceasedCheckbox.checked;

    const selectElements = document.querySelectorAll('.spouse-status-select');
    for (const select of selectElements) {
      const elSelect = select as HTMLSelectElement;
      const relId = elSelect.getAttribute('data-rel-id')!;
      const selectedStatus = elSelect.value;
      await familyService.updateRelationshipMetadata(relId, { spouseStatus: selectedStatus });
    }

    await familyService.updateMember({
      ...member,
      name: (document.getElementById('editName') as HTMLInputElement).value.trim() || member.name,
      gender: (document.getElementById('editGender') as HTMLSelectElement).value as 'M' | 'F',
      birthYear: (document.getElementById('editBirth') as HTMLInputElement).value || undefined,
      qualification: (document.getElementById('editQualification') as HTMLInputElement).value.trim() || undefined,
      profession: (document.getElementById('editProfession') as HTMLInputElement).value.trim() || undefined,
      isDeceased: isDeceasedVal,
      deathYear: isDeceasedVal ? (document.getElementById('editDeath') as HTMLInputElement).value.trim() || undefined : undefined,
      bio: (document.getElementById('editBio') as HTMLTextAreaElement).value.trim() || undefined,
      avatar: tempAvatarUrl,
      updatedAt: Date.now()
    });

    el.remove();
    await renderChart();
  });

  document.getElementById('deleteMemberBtn')?.addEventListener('click', async () => {
    if (!confirm(`Delete "${member.name}" permanently?`)) return;
    await familyService.deleteMember(member.id);
    el.remove();
    await renderChart();
  });
}

// === Helpers ===
async function populateFamilies(selectId: string) {
  const select = document.getElementById(selectId) as HTMLSelectElement;
  if (!select) return;
  const families = await familyService.getFamilies();
  select.innerHTML = '';
  if (families.length === 0) {
    select.innerHTML = '<option value="">No families</option>';
    return;
  }
  families.forEach((f) => {
    const opt = document.createElement('option');
    opt.value = f.id;
    opt.textContent = f.name;
    select.appendChild(opt);
  });
  if (!select.value) select.value = families[0].id;
}

async function updateRelatedDropdown() {
  const select = document.getElementById('memberRelatedTo') as HTMLSelectElement;
  if (!select) return;
  const familyId = (document.getElementById('familySelector') as HTMLSelectElement)?.value;
  if (!familyId) return;
  const members = await familyService.getMembersByFamily(familyId);
  const prev = select.value;
  select.innerHTML = '<option value="">Select person</option>';
  members.forEach((m) => {
    const opt = document.createElement('option');
    opt.value = m.id;
    opt.textContent = m.name;
    select.appendChild(opt);
  });
  if (prev && members.some((m) => m.id === prev)) select.value = prev;
}

// === Family Selector ===
async function setupFamilySelector() {
  const app = document.getElementById('app');
  if (!app) return;

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'display:flex;gap:10px;margin-bottom:15px;align-items:center;flex-wrap:wrap;';
  wrap.innerHTML = `
    <label style="font-weight:bold;">🌳 Family:</label>
    <select id="familySelector" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;flex:1;min-width:150px;background:var(--input-bg);color:var(--text-color);">
      <option value="">Loading...</option>
    </select>
    <button id="createFamilyBtn" style="padding:8px 16px;background:#2196F3;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">+ New Family</button>
    <button id="shareFamilyBtn" style="padding:8px 16px;background:#7B1FA2;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">🔗 Share Link</button>
    <button id="deleteFamilyBtn" style="padding:8px 16px;background:#f44336;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">🗑️ Delete Family</button>
    <button id="repairBtn" style="padding:8px 16px;background:#607D8B;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">🛠️ Repair Tree</button>
    <button id="resetViewBtn" style="padding:8px 16px;background:#00897B;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">🏠 Reset View</button>
    <button id="loadDemoBtn" style="padding:8px 16px;background:#795548;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">🌳 Load Demo Tree</button>
  `;
  app.insertBefore(wrap, app.querySelector('#chart-container'));

  await populateFamilies('familySelector');

  document.getElementById('familySelector')?.addEventListener('change', async () => {
    const id = (document.getElementById('familySelector') as HTMLSelectElement).value;
    if (id) {
      try {
        await neonService.pullFamily(id);
      } catch (err) {
        console.error("Failed to auto-pull family on switch:", err);
      }
    }
    await renderChart();
  });

    document.getElementById('createFamilyBtn')?.addEventListener('click', async () => {
    if (!currentUser) {
      alert('Please login to create a cloud-owned family.');
      openAuthModal('login');
      return;
    }
    const name = prompt('Enter family name:', 'My Family');
    if (!name) return;
    
    await familyService.createFamily(name, currentUser.id);
    await populateFamilies('familySelector');
    await renderChart();
  });

  document.getElementById('shareFamilyBtn')?.addEventListener('click', async () => {
    const select = document.getElementById('familySelector') as HTMLSelectElement;
    if (!select?.value) return;

    const shareUrl = `${window.location.origin}/share/${select.value}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!\n\n' + shareUrl);
    } catch {
      prompt('Copy this share link:', shareUrl);
    }
  });

  document.getElementById('deleteFamilyBtn')?.addEventListener('click', async () => {
    const select = document.getElementById('familySelector') as HTMLSelectElement;
    if (!select.value) return;
    if (!confirm('Delete this family and ALL members?')) return;
    await familyService.deleteFamily(select.value);
    await populateFamilies('familySelector');
    await renderChart();
  });

      document.getElementById('loadDemoBtn')?.addEventListener('click', async () => {
    if (!confirm('Load / restore the local demo "Tiafa Family Tree"? (local only, not saved to cloud)')) {
      return;
    }
    try {
      const f = await familyService.restoreDemoTree();
      await populateFamilies('familySelector');
      if (f) {
        const sel = document.getElementById('familySelector') as HTMLSelectElement;
        if (sel) sel.value = f.id;
      }
      await renderChart();
      alert('Demo tree ready (local only).');
    } catch (err) {
      console.error(err);
      alert('Failed to load demo: ' + (err as Error).message);
    }
  });

  document.getElementById('repairBtn')?.addEventListener('click', async () => {
    const select = document.getElementById('familySelector') as HTMLSelectElement;
    if (!select.value) return;
    await familyService.repairFamily(select.value);
    await renderChart();
    alert('Tree repaired: duplicates merged + parents auto-linked!');
  });

  document.getElementById('resetViewBtn')?.addEventListener('click', async () => {
    const select = document.getElementById('familySelector') as HTMLSelectElement;
    if (!select?.value) return;
    if (!chart) {
      await renderChart();
      return;
    }

    const familyId = select.value;
    const members = await familyService.getMembersByFamily(familyId);
    const relationships = await familyService.getRelationshipsByFamily(familyId);

    const childIds = new Set(
      relationships.filter((r) => r.type === 'parent').map((r) => r.targetId)
    );
    const roots = members.filter((m) => !childIds.has(m.id));

    let rootId: string | undefined;
    if (roots.length > 0) {
      rootId = (roots.find((m) => m.gender === 'M') || roots[0]).id;
    } else if (members.length > 0) {
      rootId = members[0].id;
    }

    if (!rootId) {
      alert('No members found to focus on.');
      return;
    }

    if (chart.store?.updateMainId) {
      chart.store.updateMainId(rootId);
    }
    chart.updateTree({ initial: true });
  });
}

// === Search ===
function setupSearch() {
  const app = document.getElementById('app');
  if (!app) return;

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'display:flex;gap:10px;margin:10px 0;align-items:center;flex-wrap:wrap;';
  wrap.innerHTML = `
    <input id="searchInput" type="text" placeholder="🔍 Search member..." style="padding:10px 16px;border:2px solid var(--border-color, #ddd);border-radius:8px;flex:1;min-width:200px;font-size:14px;background:var(--input-bg);color:var(--text-color);" />
    <span id="searchCount" style="color:var(--text-muted);font-size:14px;"></span>
    <button id="clearSearchBtn" style="padding:8px 16px;background:#f44336;color:white;border:none;border-radius:6px;cursor:pointer;display:none;">✕ Clear</button>
  `;

  const familySelector = document.querySelector('#app > div:first-child');
  if (familySelector) {
    familySelector.parentNode?.insertBefore(wrap, familySelector.nextSibling);
  } else {
    app.prepend(wrap);
  }

  const searchInput = document.getElementById('searchInput') as HTMLInputElement;
  const searchCount = document.getElementById('searchCount') as HTMLSpanElement;
  const clearBtn = document.getElementById('clearSearchBtn') as HTMLButtonElement;

  searchInput?.addEventListener('input', async () => {
    const query = searchInput.value.trim().toLowerCase();
    if (!query) {
      clearBtn.style.display = 'none';
      searchCount.textContent = '';
      await renderChart();
      return;
    }
    clearBtn.style.display = 'inline-block';

    const familyId = isReadOnly ? sharedFamilyId : (document.getElementById('familySelector') as HTMLSelectElement)?.value;
    if (!familyId) return;

    const allMembers = await familyService.getMembersByFamily(familyId);
    const matched = allMembers.filter((m) => m.name.toLowerCase().includes(query));
    searchCount.textContent = `Found ${matched.length} member(s)`;
  });

  clearBtn?.addEventListener('click', () => {
    searchInput.value = '';
    clearBtn.style.display = 'none';
    searchCount.textContent = '';
    renderChart();
  });
}

// === Export / Import ===
function setupExportImport() {
  const app = document.getElementById('app');
  if (!app) return;

  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;gap:10px;margin:15px 0;flex-wrap:wrap;';
  wrap.innerHTML = `
    <button id="exportBtn" style="padding:8px 16px;background:#FF9800;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">⬇️ Export JSON</button>
    <button id="importBtn" style="padding:8px 16px;background:#9C27B0;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">⬆️ Import JSON</button>
    <button id="exportPngBtn" style="padding:8px 16px;background:#03A9F4;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;margin-left:10px;">🖼️ Export PNG</button>
    <button id="exportPdfBtn" style="padding:8px 16px;background:#E91E63;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;">📄 Export PDF</button>
    <input type="file" id="importFileInput" accept=".json" style="display:none;" />
  `;

  const familySelector = document.querySelector('#app > div:first-child');
  if (familySelector) {
    familySelector.parentNode?.insertBefore(wrap, familySelector.nextSibling);
  } else {
    app.prepend(wrap);
  }

    // --- PNG / PDF EXPORT LOGIC ---
    // === PNG / PDF Export ===
    const doExportImage = async (format: 'png' | 'pdf') => {
    const chartEl = document.getElementById('chart-container');
    if (!chartEl) {
      alert('No chart found to export!');
      return;
    }

    const btnPng = document.getElementById('exportPngBtn') as HTMLButtonElement | null;
    const btnPdf = document.getElementById('exportPdfBtn') as HTMLButtonElement | null;
    if (btnPng) btnPng.disabled = true;
    if (btnPdf) btnPdf.disabled = true;

    // Temporary export brand bar
    let brandBar: HTMLDivElement | null = null;

    try {
      // @ts-ignore
      if (typeof html2canvas === 'undefined') {
        alert('Export library loading... Please refresh and try again.');
        return;
      }

      // Dynamic site name (future-proof)
      const siteName =
        (document.querySelector('h1')?.textContent || document.title || 'TIAFA Family Tree Maker')
          .replace(/\s+/g, ' ')
          .trim();

      const host = window.location.host || 'familytree.tiafa.cc.cd';
      const familyName =
        (document.getElementById('familySelector') as HTMLSelectElement)?.selectedOptions?.[0]?.textContent?.trim() ||
        'Family Tree';

      const generatedOn = new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
        timeZoneName: 'short'
      }).format(new Date());

      // Inject footer overlay inside chart for capture only
      brandBar = document.createElement('div');
      brandBar.id = 'export-brand-bar';
      brandBar.style.cssText = `
        position:absolute; left:0; right:0; bottom:0; z-index:20;
        padding:10px 14px; box-sizing:border-box;
        background:rgba(0,0,0,0.72); color:#fff;
        font-family:Segoe UI, Arial, sans-serif;
        border-top:1px solid rgba(255,255,255,0.15);
        pointer-events:none;
      `;
      brandBar.innerHTML = `
        <div style="display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;align-items:flex-end;">
          <div style="min-width:220px;">
            <div style="font-size:13px;font-weight:700;letter-spacing:0.2px;">🌳 ${siteName}</div>
            <div style="font-size:11px;opacity:0.9;margin-top:2px;">👨‍👩‍👧‍👦 ${familyName} · 🔗 ${host}</div>
          </div>
          <div style="text-align:right;min-width:220px;">
            <div style="font-size:11px;opacity:0.95;">🗓️ Generated on ${generatedOn}</div>
            <div style="font-size:10px;opacity:0.8;margin-top:2px;">Developed by Varun Kumar · Educational family record use</div>
          </div>
        </div>
      `;

      // chart container relative hona chahiye
      const prevPos = chartEl.style.position;
      if (!prevPos || prevPos === 'static') chartEl.style.position = 'relative';
      chartEl.appendChild(brandBar);

      // @ts-ignore
      const canvas = await html2canvas(chartEl, {
        backgroundColor: document.body.classList.contains('dark-mode') ? '#1e1e1e' : '#212121',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });

      // cleanup brand bar
      brandBar.remove();
      brandBar = null;
      chartEl.style.position = prevPos;

      const imgData = canvas.toDataURL('image/png');
      const fileBase = `TIAFA-Family-Tree-${new Date().toISOString().slice(0, 10)}`;

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `${fileBase}.png`;
        link.href = imgData;
        link.click();
      } else {
        // @ts-ignore
        const { jsPDF } = window.jspdf;
        const orientation = canvas.width >= canvas.height ? 'landscape' : 'portrait';
        const pdf = new jsPDF({
          orientation,
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${fileBase}.pdf`);
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + (err as Error).message);
    } finally {
      if (brandBar && brandBar.parentElement) brandBar.remove();
      if (btnPng) btnPng.disabled = false;
      if (btnPdf) btnPdf.disabled = false;
    }
  };

  wrap.querySelector('#exportPngBtn')?.addEventListener('click', () => doExportImage('png'));
  wrap.querySelector('#exportPdfBtn')?.addEventListener('click', () => doExportImage('pdf'));

  wrap.querySelector('#exportBtn')?.addEventListener('click', async () => {
    const families = await familyService.getFamilies();
    if (families.length === 0) {
      alert('No data to export!');
      return;
    }
    const exportData = {
      exportedAt: new Date().toISOString(),
      families,
      members: await db.getAll('members'),
      relationships: await db.getAll('relationships')
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `family-tree-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  });

  wrap.querySelector('#importBtn')?.addEventListener('click', () => {
    (document.getElementById('importFileInput') as HTMLInputElement)?.click();
  });

  wrap.querySelector('#importFileInput')?.addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      if (!data.families || !data.members || !data.relationships) {
        throw new Error('Invalid data format!');
      }
      if (
        !confirm(
          `Import ${data.families.length} family(s), ${data.members.length} members?`
        )
      ) {
        input.value = '';
        return;
      }
      await familyService.clearAllData();
      for (const f of data.families) await db.add('families', f);
      for (const m of data.members) await db.add('members', m);
      for (const r of data.relationships) await db.add('relationships', r);
      input.value = '';
      await populateFamilies('familySelector');
      await renderChart();
    } catch (err) {
      alert('Import failed: ' + (err as Error).message);
      input.value = '';
    }
  });
}

// === Add Member Form ===
function setupAddMemberForm() {
  const app = document.getElementById('app');
  if (!app) return;

  const form = document.createElement('div');
  form.style.cssText =
    'margin:20px 0;padding:20px;background:var(--card-bg, white);border-radius:12px;box-shadow:var(--shadow);border:1px solid var(--border-color, #eee);';
  
  let uploadedAvatarUrl = '';

  form.innerHTML = `
    <h3 style="margin:0 0 15px 0;font-weight:700;">➕ Add Family Member</h3>
    
    <div style="display:flex;gap:15px;flex-wrap:wrap;align-items:end;margin-bottom:15px;">
      <div>
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Full Name</label>
        <input id="memberName" type="text" placeholder="e.g. Twisha" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:180px;background:var(--input-bg);color:var(--text-color);" />
      </div>
      <div>
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Gender</label>
        <select id="memberGender" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);color:var(--text-color);">
          <option value="M">Male</option>
          <option value="F">Female</option>
        </select>
      </div>
      <div>
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Birth Year</label>
        <input id="memberBirth" type="text" placeholder="e.g. 2009" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:100px;background:var(--input-bg);color:var(--text-color);" />
      </div>
      <div>
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Relationship</label>
        <select id="memberRelationship" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:120px;background:var(--input-bg);color:var(--text-color);">
          <option value="none">None</option>
          <option value="child">Child of...</option>
          <option value="parent">Parent of...</option>
          <option value="spouse">Spouse of...</option>
        </select>
      </div>
      <div>
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">With</label>
        <select id="memberRelatedTo" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:140px;background:var(--input-bg);color:var(--text-color);">
          <option value="">Select person</option>
        </select>
      </div>

      <div id="addSpouseStatusWrapper" style="display:none;">
        <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Spouse Status</label>
        <select id="memberSpouseStatus" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:120px;background:var(--input-bg);color:var(--text-color);">
          <option value="married">Married 💍</option>
          <option value="divorced">Divorced 💔</option>
          <option value="separated">Separated 🤝</option>
          <option value="widowed">Widowed 🕊️</option>
        </select>
      </div>
      
      <div style="display:flex;align-items:center;gap:10px;background:var(--bg-color, #f9f9f9);padding:5px 10px;border-radius:8px;border:1px solid var(--border-color, #eee);">
        <div>
          <label style="display:block;font-size:11px;color:var(--text-muted, #666);margin-bottom:2px;font-weight:600;">Photo (Optional)</label>
          <input type="file" id="memberPhoto" accept="image/*" style="display:none;" />
          <button id="triggerPhotoUpload" style="padding:6px 12px;background:#eee;color:#333;border:1px solid #ccc;border-radius:4px;font-size:11px;cursor:pointer;font-weight:bold;">Upload</button>
        </div>
        <div id="photoPreview" style="width:45px;height:45px;border-radius:50%;border:1px solid #ddd;display:none;background-size:cover;background-position:center;box-shadow:0 1px 4px rgba(0,0,0,0.1);"></div>
      </div>
    </div>

    <details style="margin-bottom:15px;background:var(--bg-color, #fdfdfd);border:1px solid var(--border-color, #eef0f2);border-radius:8px;padding:8px 12px;">
      <summary style="font-size:13px;font-weight:bold;cursor:pointer;color:#2196F3;outline:none;user-select:none;">🎨 More Details (Bio, Qualification, Living Status)</summary>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-top:12px;padding-top:10px;border-top:1px solid #eee;">
        <div>
          <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Qualification</label>
          <input id="memberQualification" type="text" placeholder="e.g. M.B.B.S, B.Com" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
        </div>
        <div>
          <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Profession</label>
          <input id="memberProfession" type="text" placeholder="e.g. Banker, Retired" style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);box-sizing:border-box;" />
        </div>
        <div style="grid-column: span 2; display:flex; align-items:center; gap:20px;">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;font-weight:600;">
            <input type="checkbox" id="memberIsDeceased" style="transform:scale(1.1);" />
            🕊️ Is Deceased
          </label>
          <div id="addDeathYearContainer" style="display:none;flex:1;">
            <input id="memberDeath" type="text" placeholder="Death Year (e.g. 2018)" style="padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;width:150px;background:var(--input-bg);color:var(--text-color);" />
          </div>
        </div>
        <div style="grid-column: span 2;">
          <label style="display:block;font-size:12px;color:var(--text-muted, #666);margin-bottom:4px;">Biography / Notes</label>
          <textarea id="memberBio" rows="2" placeholder="Tell a story or add details about their legacy..." style="width:100%;padding:8px 12px;border:1px solid var(--border-color, #ddd);border-radius:6px;background:var(--input-bg);font-family:inherit;font-size:13px;box-sizing:border-box;resize:none;"></textarea>
        </div>
      </div>
    </details>

    <button id="addMemberBtn" style="padding:8px 20px;background:#4CAF50;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:bold;height:38px;width:100%;">Add Member to Tree</button>
  `;

  app.insertBefore(form, app.querySelector('#chart-container'));

  const fileInput = document.getElementById('memberPhoto') as HTMLInputElement;
  const triggerBtn = document.getElementById('triggerPhotoUpload');
  const previewEl = document.getElementById('photoPreview') as HTMLDivElement;
  const relSelect = document.getElementById('memberRelationship') as HTMLSelectElement;
  const spouseStatusWrapper = document.getElementById('addSpouseStatusWrapper') as HTMLDivElement;

  relSelect.addEventListener('change', () => {
    spouseStatusWrapper.style.display = relSelect.value === 'spouse' ? 'block' : 'none';
  });

  const isDeceasedCheckbox = document.getElementById('memberIsDeceased') as HTMLInputElement;
  const deathContainer = document.getElementById('addDeathYearContainer') as HTMLDivElement;
  isDeceasedCheckbox.addEventListener('change', () => {
    deathContainer.style.display = isDeceasedCheckbox.checked ? 'block' : 'none';
  });

  triggerBtn?.addEventListener('click', () => fileInput.click());
  fileInput?.addEventListener('change', async () => {
    await handleImageUploadFlow(fileInput, previewEl, (url) => {
      uploadedAvatarUrl = url;
    });
  });

  document.getElementById('addMemberBtn')?.addEventListener('click', async () => {
    const name = (document.getElementById('memberName') as HTMLInputElement).value.trim();
    const gender = (document.getElementById('memberGender') as HTMLSelectElement).value as 'M' | 'F';
    const birthYear = (document.getElementById('memberBirth') as HTMLInputElement).value.trim();
    const relType = (document.getElementById('memberRelationship') as HTMLSelectElement).value;
    const relatedId = (document.getElementById('memberRelatedTo') as HTMLSelectElement).value;
    const familyId = (document.getElementById('familySelector') as HTMLSelectElement)?.value;

    const qualification = (document.getElementById('memberQualification') as HTMLInputElement).value.trim() || undefined;
    const profession = (document.getElementById('memberProfession') as HTMLInputElement).value.trim() || undefined;
    const isDeceased = isDeceasedCheckbox.checked;
    const deathYear = isDeceased ? (document.getElementById('memberDeath') as HTMLInputElement).value.trim() || undefined : undefined;
    const bio = (document.getElementById('memberBio') as HTMLTextAreaElement).value.trim() || undefined;

    if (!name) {
      alert('Please enter a name!');
      return;
    }
    if (!familyId) {
      alert('No family selected!');
      return;
    }
    if (relType !== 'none' && !relatedId) {
      alert('Select a person for the relationship (With).');
      return;
    }

    try {
      const newMember = await familyService.createMember(
        familyId,
        name,
        gender,
        birthYear || undefined,
        deathYear,
        bio,
        uploadedAvatarUrl || undefined,
        qualification,
        profession,
        isDeceased
      );

      let createdRelationship: Relationship | null = null;

      if (relType === 'child' && relatedId) {
        createdRelationship = await familyService.createRelationship(familyId, relatedId, newMember.id, 'parent');
      } else if (relType === 'parent' && relatedId) {
        createdRelationship = await familyService.createRelationship(familyId, newMember.id, relatedId, 'parent');
      } else if (relType === 'spouse' && relatedId) {
        createdRelationship = await familyService.createRelationship(familyId, newMember.id, relatedId, 'spouse');
        
        const spouseStatus = (document.getElementById('memberSpouseStatus') as HTMLSelectElement).value;
        if (createdRelationship && spouseStatus) {
          await familyService.updateRelationshipMetadata(createdRelationship.id, { spouseStatus });
        }
      }

      (document.getElementById('memberName') as HTMLInputElement).value = '';
      (document.getElementById('memberBirth') as HTMLInputElement).value = '';
      (document.getElementById('memberRelationship') as HTMLSelectElement).value = 'none';
      (document.getElementById('memberRelatedTo') as HTMLSelectElement).value = '';
      (document.getElementById('memberQualification') as HTMLInputElement).value = '';
      (document.getElementById('memberProfession') as HTMLInputElement).value = '';
      isDeceasedCheckbox.checked = false;
      (document.getElementById('memberDeath') as HTMLInputElement).value = '';
      (document.getElementById('memberBio') as HTMLTextAreaElement).value = '';
      
      spouseStatusWrapper.style.display = 'none';
      deathContainer.style.display = 'none';

      uploadedAvatarUrl = '';
      previewEl.style.display = 'none';
      previewEl.style.backgroundImage = 'none';
      fileInput.value = '';

      await renderChart();
      console.log(`%c✅ Added member: ${name} with extra options successfully!`, 'color: #4CAF50; font-weight: bold;');
    } catch (error) {
      console.error(error);
      alert('Error: ' + (error as Error).message);
    }
  });
}

function updateAuthHeader() {
  const slot = document.getElementById('authHeaderSlot');
  if (!slot) return;

  if (currentUser) {
    slot.innerHTML = `
      <span style="font-size:13px;color:var(--text-color);opacity:0.9;">👤 ${currentUser.username}</span>
      <button id="logoutBtn" style="padding:8px 14px;border-radius:20px;border:1px solid var(--border-color);background:var(--card-bg);color:var(--text-color);cursor:pointer;font-weight:600;">Logout</button>
    `;
    document.getElementById('logoutBtn')?.addEventListener('click', async () => {
      try {
        await authService.logout();
      } catch {}
      currentUser = null;
      updateAuthHeader();
      alert('Logged out');
    });
  } else {
    slot.innerHTML = `
      <button id="openLoginBtn" style="padding:8px 14px;border-radius:20px;border:none;background:#2196F3;color:white;cursor:pointer;font-weight:600;">Login</button>
      <button id="openSignupBtn" style="padding:8px 14px;border-radius:20px;border:none;background:#7B1FA2;color:white;cursor:pointer;font-weight:600;">Sign up</button>
    `;
    document.getElementById('openLoginBtn')?.addEventListener('click', () => openAuthModal('login'));
    document.getElementById('openSignupBtn')?.addEventListener('click', () => openAuthModal('signup'));
  }
}

function openAuthModal(mode: 'login' | 'signup') {
  document.getElementById('authModal')?.remove();
  const bg = LOGIN_BG_IMAGES[Math.floor(Math.random() * LOGIN_BG_IMAGES.length)];

  const el = document.createElement('div');
  el.id = 'authModal';
  el.style.cssText = `
    position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;
    background:rgba(0,0,0,0.55);backdrop-filter:blur(3px);padding:16px;`;

  el.innerHTML = `
    <div style="width:min(420px,100%);border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.45);border:1px solid rgba(255,255,255,0.12);">
      <div style="height:110px;background-image:url('${bg}');background-size:cover;background-position:center;position:relative;">
        <button id="authClose" style="position:absolute;top:8px;right:10px;border:none;background:rgba(0,0,0,0.5);color:#fff;width:32px;height:32px;border-radius:50%;cursor:pointer;font-size:18px;">×</button>
      </div>
      <div style="background:var(--card-bg,#1e1e1e);color:var(--text-color,#eee);padding:20px;">
        <h3 style="margin:0 0 12px 0;">${mode === 'login' ? '🔐 Login' : '✨ Create Account'}</h3>
        
        ${mode === 'signup' ? `
          <div style="background:rgba(244,67,54,0.12);border:1px solid rgba(244,67,54,0.55);color:#ff8a80;padding:10px 12px;border-radius:8px;margin-bottom:12px;font-size:12px;line-height:1.4;">
            <strong style="color:#ff5252;">⚠️ IMPORTANT: Password recovery is NOT supported.</strong><br/>
            Please <strong>save your Username / Email + Password</strong> safely.<br/>
            Lost credentials = new account only. No reset emails.
          </div>
          <label style="font-size:12px;opacity:0.8;">Username</label>
          <input id="authUsername" type="text" placeholder="min 3 chars" style="width:100%;margin:4px 0 10px;padding:10px;border-radius:8px;border:1px solid var(--border-color,#444);background:var(--input-bg,#2a2a2a);color:inherit;box-sizing:border-box;" />
          <label style="font-size:12px;opacity:0.8;">Email</label>
          <input id="authEmail" type="email" placeholder="you@email.com" style="width:100%;margin:4px 0 10px;padding:10px;border-radius:8px;border:1px solid var(--border-color,#444);background:var(--input-bg,#2a2a2a);color:inherit;box-sizing:border-box;" />
        ` : `
          <label style="font-size:12px;opacity:0.8;">Username or Email</label>
          <input id="authUserOrEmail" type="text" placeholder="username or email" style="width:100%;margin:4px 0 10px;padding:10px;border-radius:8px;border:1px solid var(--border-color,#444);background:var(--input-bg,#2a2a2a);color:inherit;box-sizing:border-box;" />
        `}

        <label style="font-size:12px;opacity:0.8;">Password</label>
        <input id="authPassword" type="password" placeholder="min 6 chars" style="width:100%;margin:4px 0 14px;padding:10px;border-radius:8px;border:1px solid var(--border-color,#444);background:var(--input-bg,#2a2a2a);color:inherit;box-sizing:border-box;" />

        <div id="authError" style="display:none;color:#ff8a80;font-size:12px;margin-bottom:10px;"></div>

        <button id="authSubmit" style="width:100%;padding:12px;border:none;border-radius:8px;background:${mode === 'login' ? '#2196F3' : '#7B1FA2'};color:white;font-weight:700;cursor:pointer;">
          ${mode === 'login' ? 'Login' : 'Sign up'}
        </button>
        <button id="authSwitch" style="width:100%;margin-top:10px;padding:8px;border:none;background:transparent;color:#90caf9;cursor:pointer;font-size:13px;">
          ${mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Login'}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(el);

  const showErr = (msg: string) => {
    const box = document.getElementById('authError');
    if (!box) return;
    box.style.display = 'block';
    box.textContent = msg;
  };

  document.getElementById('authClose')?.addEventListener('click', () => el.remove());
  el.addEventListener('click', (e) => { if (e.target === el) el.remove(); });
  document.getElementById('authSwitch')?.addEventListener('click', () => {
    el.remove();
    openAuthModal(mode === 'login' ? 'signup' : 'login');
  });

  document.getElementById('authSubmit')?.addEventListener('click', async () => {
    const btn = document.getElementById('authSubmit') as HTMLButtonElement;
    const password = (document.getElementById('authPassword') as HTMLInputElement).value;
    btn.disabled = true;
    try {
      if (mode === 'signup') {
        const username = (document.getElementById('authUsername') as HTMLInputElement).value.trim();
        const email = (document.getElementById('authEmail') as HTMLInputElement).value.trim();
        currentUser = await authService.signup(username, email, password);
      } else {
        const usernameOrEmail = (document.getElementById('authUserOrEmail') as HTMLInputElement).value.trim();
        currentUser = await authService.login(usernameOrEmail, password);
      }
      updateAuthHeader();
      el.remove();
      alert(mode === 'login' ? `Welcome, ${currentUser.username}!` : 'Account created. You are logged in.');
    } catch (err: any) {
      showErr(err.message || 'Failed');
    } finally {
      btn.disabled = false;
    }
  });
}

// === Dark Mode Theme Logic ===
function setupThemeToggle() {
  const toggleBtn = document.getElementById('themeToggleBtn');
  if (!toggleBtn) return;

  const savedTheme = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
    document.body.classList.add('dark-mode');
    toggleBtn.innerHTML = '☀️ Light Mode';
  } else {
    document.body.classList.remove('dark-mode');
    toggleBtn.innerHTML = '🌙 Dark Mode';
  }

  toggleBtn.onclick = () => {
    const isDark = document.body.classList.toggle('dark-mode');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    toggleBtn.innerHTML = isDark ? '☀️ Light Mode' : '🌙 Dark Mode';
  };
}

// Run theme setup immediately
setupThemeToggle();

// === Share Mode Initializer ===
async function initReadOnlyMode(familyId: string) {
  console.log('🔗 Share mode active for family:', familyId);
  sharedFamilyId = familyId;
  isReadOnly = true;

  try {
    await neonService.pullFamily(familyId);
  } catch (err) {
    console.error('Share pull failed:', err);
  }

  const family = await familyService.getFamily(familyId);
  if (!family) {
    const container = document.getElementById('chart-container');
    if (container) {
      container.innerHTML = `
        <div style="color:white;padding:40px;text-align:center;">
          <h2>⚠️ Shared Tree Not Found</h2>
          <p style="margin-top:10px;color:#aaa;">The family link you followed might be incorrect or deleted.</p>
          <a href="/" style="display:inline-block;margin-top:20px;padding:10px 20px;background:#2196F3;color:white;border-radius:6px;text-decoration:none;font-weight:bold;">Go to Home Page</a>
        </div>`;
    }
    return;
  }

  showReadOnlyBadge(family.name);
  setupSearch();
  await renderChart();
}

// === Boot Application ===
async function init() {
  // Listen for auth-required events from neonService
  window.addEventListener('tiafa:auth-required', () => openAuthModal('login'));
  const route = detectShareRoute();

  if (route.isShare && route.familyId) {
    await initReadOnlyMode(route.familyId);
    return;
  }

  // Normal owner/editor mode
  isReadOnly = false;
  sharedFamilyId = null;

  await setupFamilySelector();
  setupSearch();
  setupExportImport();
  setupAddMemberForm();
  loadFooterMetadata();

  const select = document.getElementById('familySelector') as HTMLSelectElement;
  const fid = select?.value;
  if (fid) {
    try {
      await neonService.pullFamily(fid);
    } catch (err) {
      console.error("Failed to auto-pull family on init:", err);
    }
  }

  currentUser = await authService.me();
    updateAuthHeader();

  await renderChart();
}

async function loadFooterMetadata() {
  try {
    const res = await fetch('/api/version');
    const data = await res.json();
    
    document.getElementById('meta-id')!.innerText = data.id || 'Dev';
    
    if (data.timestamp) {
      // Format as "08 Aug 2026, 12:03 am IST"
      const dateStr = new Intl.DateTimeFormat('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'Asia/Kolkata', timeZoneName: 'short'
      }).format(new Date(data.timestamp));
      
      document.getElementById('meta-time')!.innerText = dateStr;
    }
  } catch (err) {
    document.getElementById('meta-time')!.innerText = 'Local Env';
    console.log('Version metadata only available on production build.');
  }
}

// init() ke andar loadFooterMetadata() call kar dena

init();